import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
} from "@mui/material";
import { useAuthContext } from "../contexts/AuthContext";
import { AuthLayout } from "../components/Layouts";
import { createLoginStyles } from "./styles";
import { useTheme } from "@mui/material/styles";
import { resetPassword } from "../api/auth";
import { ROUTES } from "../constants/routes";

/**
 * Reset Password Page Component
 *
 * Handles password reset using a token and email from URL parameters.
 * Allows users to set a new password after clicking a reset link.
 */
const ResetPassword: React.FC = () => {
  // Styles
  const theme = useTheme();
  const loginStyles = createLoginStyles(theme);

  // Hooks
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get token and email from URL parameters
  const token = searchParams.get("token") || "";
  const emailParam = searchParams.get("email") || "";

  // State
  const [email, setEmail] = useState(emailParam);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("");

  // Clear passwords on unmount for security
  useEffect(() => {
    return () => {
      setPassword("");
      setPasswordConfirmation("");
    };
  }, []);

  // Handle password reset form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setValidationErrors({});
    setIsLoading(true);

    try {
      const response = await resetPassword(
        token,
        email,
        password,
        passwordConfirmation
      );
      setStatus(response.status);
      navigate(ROUTES.LOGIN, {
        state: {
          message:
            "Password reset successfully! Please log in with your new password.",
        },
      });
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // If already authenticated, redirect to home
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // If no token provided, redirect to forgot password
  if (!token) {
    return <Navigate to={ROUTES.FORGOT_PASSWORD} replace />;
  }

  // Render
  return (
    <AuthLayout>
      <title>Reset password - Materiom Commons</title>
      <Container sx={loginStyles.container}>
        <Paper elevation={24} sx={loginStyles.paper}>
          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={loginStyles.errorAlert}>
              {error}
            </Alert>
          )}

          {/* Validation Errors */}
          {Object.keys(validationErrors).length > 0 && (
            <Alert severity="error" sx={loginStyles.errorAlert}>
              <Box>
                {Object.entries(validationErrors).map(([field, errors]) => (
                  <Box key={field}>
                    {errors.map((error, index) => (
                      <Typography key={index} variant="body2">
                        {error}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Box>
            </Alert>
          )}

          {/* Reset Password Form */}
          <Box component="form" onSubmit={handleSubmit}>
            {/* Email Input */}
            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                variant="outlined"
                sx={loginStyles.textField}
                disabled // Email should be read-only from the reset link
              />
            </Box>

            {/* Password Input */}
            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                Password
              </Typography>
              <TextField
                fullWidth
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                autoFocus
                variant="outlined"
                sx={loginStyles.textField}
                error={!!validationErrors.password}
              />
            </Box>

            {/* Confirm Password Input */}
            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                Confirm Password
              </Typography>
              <TextField
                fullWidth
                type="password"
                name="password_confirmation"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                required
                autoComplete="new-password"
                variant="outlined"
                sx={loginStyles.textField}
                error={!!validationErrors.password_confirmation}
              />
            </Box>

            {/* Submit Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={loginStyles.loginButton}
                disableRipple
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </AuthLayout>
  );
};

export default ResetPassword;
